import { type ParsedCsv, type ParsedRow } from './parse';
import { phoneProblem, type PhoneProblem } from '~/lib/phone';
import { canonicalEnumValue, sameLabel, splitList, type CsvField, type CsvSchema } from './schema';

// Invalid rows carry their errors rather than failing the whole upload, so a few bad
// rows can be fixed in place.

/** A problem with one cell, named so the user can find and fix it. */
export type CellError = {
  /** Field key, so the table can mark the offending column. */
  key: string;
  message: string;
  /** Another row names the same record; the fix is choosing between rows. */
  duplicate?: true;
};

export type ValidatedRow = {
  /** 1-based line in the uploaded file. */
  line: number;
  /** Field key to cell value, present whether or not the row validated. */
  values: Record<string, string>;
  errors: CellError[];
};

/** A problem with the file as a whole, which makes row-level errors meaningless. */
export type StructuralError = { message: string };

export type ValidationResult = {
  structuralErrors: StructuralError[];
  /** Header columns not in the schema. Reported, not fatal — extra data is ignored. */
  unknownColumns: string[];
  rows: ValidatedRow[];
};

const BOOLEAN_VALUES = new Set(['true', 'false', 'yes', 'no', '1', '0']);
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Rejects characters outside `field.allow` in values read from a file (the grid blocks typing them). */
const refusedCharacters = (field: CsvField, value: string): string | undefined => {
  const allow = field.allow;
  const refused = allow ? [...new Set([...value].filter((char) => !allow.test(char)))] : [];
  return refused.length > 0
    ? `${field.label} can't contain ${refused.map((char) => `"${char}"`).join(', ')}`
    : undefined;
};

const phoneMessage = (label: string, problem: PhoneProblem | undefined): string | undefined => {
  switch (problem) {
    case undefined:
      return undefined;
    case 'too-short':
      return `${label} is missing digits`;
    case 'too-long':
      return `${label} has too many digits`;
    case 'not-a-number':
      return `${label} must be a phone number`;
    case 'unknown-country':
      return `${label} has a country code that doesn't exist`;
    case 'invalid':
      return `${label} isn't a real phone number`;
  }
};

/** Describes why one value does not satisfy `field`, or undefined when it does. */
const checkValue = (field: CsvField, value: string): string | undefined => {
  switch (field.type) {
    case 'number':
      return Number.isFinite(Number(value)) ? undefined : `${field.label} must be a number`;
    case 'boolean':
      return BOOLEAN_VALUES.has(value.toLowerCase())
        ? undefined
        : `${field.label} must be true or false`;
    case 'date':
      return ISO_DATE.test(value) && !Number.isNaN(Date.parse(value))
        ? undefined
        : `${field.label} must be a date as YYYY-MM-DD`;
    case 'email':
      return EMAIL.test(value) ? undefined : `${field.label} must be an email address`;
    case 'phone':
      // Same library and default region as the server, so a bad number is caught per cell before save.
      return phoneMessage(field.label, phoneProblem(value));
    case 'enum': {
      if (canonicalEnumValue(field, value)) {
        return undefined;
      }
      return `"${value}" is not a valid value for ${field.label}`;
    }
    case 'string':
      return undefined;
  }
};

/**
 * Checks one cell, given its row's values by key; list cells are checked per element so the
 * error names the bad value.
 */
export const validateCell = (
  field: CsvField,
  raw: string,
  row: Record<string, string>
): CellError[] => {
  const value = raw.trim();

  if (value === '') {
    const missing = field.required ? `${field.label} is required` : field.requiredFor?.(row);
    return missing ? [{ key: field.key, message: missing }] : [];
  }

  const parts = field.multiple ? splitList(value) : [value];

  const errors: CellError[] = [];
  for (const part of parts) {
    const failure =
      checkValue(field, part) ?? refusedCharacters(field, part) ?? field.validate?.(part);
    if (failure) {
      errors.push({ key: field.key, message: failure });
    }
  }
  return errors;
};

/** Reads a row's cells into field keys, using the header to locate each column. */
const readValues = (
  schema: CsvSchema,
  header: string[],
  row: ParsedRow
): Record<string, string> => {
  const values: Record<string, string> = {};
  for (const field of schema.fields) {
    // When two headers name the same field, the first wins.
    const column = header.findIndex((cell) => sameLabel(cell, field.label));
    values[field.key] = column === -1 ? '' : (row.cells[column] ?? '');
  }
  return values;
};

/** Checks the header alone, so a wrong file can be rejected before the rest is read. */
export const headerProblem = (schema: CsvSchema, header: string[]): StructuralError | undefined => {
  if (header.length === 0) {
    return { message: 'This file is empty.' };
  }
  const missing = schema.fields
    .filter((field) => field.required && !header.some((cell) => sameLabel(cell, field.label)))
    .map((field) => field.label);
  if (missing.length > 0) {
    return {
      message: `This file is missing ${missing.length === 1 ? 'a required column' : 'required columns'}: ${missing.join(', ')}. Download the template to start from the right columns.`
    };
  }
  return undefined;
};

const findStructuralErrors = (schema: CsvSchema, parsed: ParsedCsv): StructuralError[] => {
  const problem = headerProblem(schema, parsed.header);
  if (problem) {
    return [problem];
  }
  if (parsed.rows.length === 0) {
    return [{ message: 'This file has column headers but no rows.' }];
  }
  return [];
};

/** Structural problems skip row validation, so one missing column isn't reported on every row. */
export const validateCsv = (schema: CsvSchema, parsed: ParsedCsv): ValidationResult => {
  const structuralErrors = findStructuralErrors(schema, parsed);
  const unknownColumns = parsed.header.filter(
    (column) => !schema.fields.some((field) => sameLabel(field.label, column))
  );

  if (structuralErrors.length > 0) {
    return { structuralErrors, unknownColumns, rows: [] };
  }

  const rows = parsed.rows.map((row) => {
    const values = readValues(schema, parsed.header, row);
    const errors = schema.fields.flatMap((field) => validateCell(field, values[field.key], values));
    return { line: row.line, values, errors };
  });

  return {
    structuralErrors,
    unknownColumns,
    rows
  };
};
